import {
  Box,
  CircularProgress,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useAuth } from '../../../contexts/authContext';
import { hasWorkspaceAccess, Roles } from '../../../helpers/access';
import { findRoundScheduleEntry, formatScheduleDateTime } from '../../../helpers/roundSchedule';
import { updateTotalPoints } from '../../../features/game/gameSlice';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    borderBottom: 'none',
    fontSize: 12
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    borderBottom: 'none'
  }
}));

const SectionScoreCard = ({
  section,
  teams,
  round,
  isOverlay = false,
  event: eventProp,
  onGameClick,
  allowPointEditing = true,
  isGameClickable
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Local state for teams to allow instant updates
  const [localTeams, setLocalTeams] = useState(teams);

  // Get user and role from Redux
  const { authenticated, currentUserProfile, currentWorkspace } = useAuth();
  const isAdmin =
    allowPointEditing &&
    authenticated &&
    hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.ADMIN);

  // Local state for editing
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const editLoading = useSelector((state) => state.game.updatingTotalPoints);
  const storeEvent = useSelector((state) => state.event.event);
  const event = eventProp || storeEvent;

  useEffect(() => {
    setLocalTeams(teams);
  }, [teams]);

  if (!section) {
    return <CircularProgress />;
  }

  const overlayFontSize = isOverlay ? 14 : 12;
  const overlayHeaderFontSize = isOverlay ? 15 : 12;
  const overlayTeamNameSize = isOverlay ? 13 : 10;
  const overlayTeamAgainstSize = isOverlay ? 12 : 10;
  const overlayScoreValueSize = isOverlay ? 15 : 12;
  const headerLabel = round ? `Round ${round}` : 'Team';
  const roundScheduleText = formatScheduleDateTime(
    findRoundScheduleEntry(event?.roundSchedule, round)
  );

  const matchRows = (() => {
    const groupedByGame = new Map();

    localTeams.forEach((team) => {
      const gameKey = team.game || `${team.round}-${team.teamId}-${team.teamAgainstName}`;
      if (!groupedByGame.has(gameKey)) {
        groupedByGame.set(gameKey, []);
      }
      groupedByGame.get(gameKey).push(team);
    });

    const scoreDelta = (team) => {
      const shotsWon = Number(team.totalShotsWon || 0);
      const shotsAgainst = Number(team.totalShotsAgainst || 0);
      return shotsWon - shotsAgainst;
    };

    const chooseTeamFor = (a, b) => {
      if (Number(a.totalPoints || 0) !== Number(b.totalPoints || 0)) {
        return Number(a.totalPoints || 0) > Number(b.totalPoints || 0) ? a : b;
      }

      if (Number(a.net || 0) !== Number(b.net || 0)) {
        return Number(a.net || 0) > Number(b.net || 0) ? a : b;
      }

      if (Number(a.setsWon || 0) !== Number(b.setsWon || 0)) {
        return Number(a.setsWon || 0) > Number(b.setsWon || 0) ? a : b;
      }

      if (scoreDelta(a) !== scoreDelta(b)) {
        return scoreDelta(a) > scoreDelta(b) ? a : b;
      }

      return a;
    };

    const toTimestamp = (value) => {
      const timestamp = Date.parse(value || '');
      return Number.isNaN(timestamp) ? null : timestamp;
    };

    return Array.from(groupedByGame.values())
      .map((group) => {
        if (group.length < 2) {
          return {
            ...group[0],
            teamFor: group[0],
            teamAgainst: null
          };
        }

        const [first, second] = group;
        const teamFor = chooseTeamFor(first, second);
        const teamAgainst = teamFor === first ? second : first;

        return {
          ...teamFor,
          teamFor,
          teamAgainst,
          teamName: teamFor.teamName,
          teamAgainstName: teamAgainst.teamName,
          game: teamFor.game || teamAgainst.game,
          round: teamFor.round || teamAgainst.round,
          lastGamePlayedAt: teamFor.lastGamePlayedAt || teamAgainst.lastGamePlayedAt || null
        };
      })
      .sort((a, b) => {
        const aTime = toTimestamp(a.lastGamePlayedAt);
        const bTime = toTimestamp(b.lastGamePlayedAt);

        if (aTime !== null && bTime !== null) {
          return bTime - aTime;
        }

        if (bTime !== null) {
          return 1;
        }

        if (aTime !== null) {
          return -1;
        }

        return 0;
      });
  })();

  const showExtraEndColumn = matchRows.some(
    (match) => match.teamFor?.extraEndEnabled || match.teamAgainst?.extraEndEnabled
  );

  const setBox = (match) => {
    const teamFor = match.teamFor || match;
    const teamAgainst = match.teamAgainst;
    const hasAgainst = Boolean(teamAgainst);

    const teamForSets = teamFor.sets || [];
    const teamAgainstSets = hasAgainst ? teamAgainst.sets || [] : [];

    return (
      <Box display="flex" flexDirection={'column'} alignItems={'center'} justifyContent={'center'}>
        <Box display="flex" flexDirection={'row'} alignItems={'center'} justifyContent={'center'}>
          {teamForSets.map((set, index) => (
            <Box
              key={`set-${index}`}
              display="flex"
              flexDirection={'column'}
              alignItems={'center'}
              p={isOverlay ? 1.25 : 1}>
              <Typography fontSize={overlayScoreValueSize}>{set.shotsWon}</Typography>
              <Typography fontSize={overlayScoreValueSize}>
                {hasAgainst
                  ? (teamAgainstSets[index]?.shotsWon ?? set.shotsAgainst)
                  : set.shotsAgainst}
              </Typography>
            </Box>
          ))}
          <Box
            display="flex"
            flexDirection={'column'}
            alignItems={'center'}
            p={isOverlay ? 1.25 : 1}>
            <Typography fontSize={overlayScoreValueSize}>({teamFor.totalShotsWon})</Typography>
            <Typography fontSize={overlayScoreValueSize}>
              ({hasAgainst ? teamAgainst?.totalShotsWon : teamFor.totalShotsAgainst})
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderExtraEndCell = (match) => {
    const topValue = Number(match.teamFor?.extraEndPoints || 0);
    const bottomValue = Number(match.teamAgainst?.extraEndPoints || 0);

    return (
      <Box display="flex" flexDirection={'column'} alignItems={'center'} gap={0.25}>
        <Typography fontSize={overlayScoreValueSize} color="success.main" fontWeight={'bold'}>
          {topValue}
        </Typography>
        <Typography fontSize={overlayScoreValueSize} color="success.main" fontWeight={'bold'}>
          {bottomValue}
        </Typography>
      </Box>
    );
  };

  const renderEditableTotalValue = (team) => {
    if (!team) return '-';

    if (isAdmin && editingTeamId === team.teamId) {
      if (editLoading) {
        return (
          <Box display="flex" alignItems="center" justifyContent="center">
            <CircularProgress size={20} />
          </Box>
        );
      }
      return (
        <input
          type="number"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={() => handleSave(team.teamId, team.game)}
          autoFocus
          style={{ width: 40, fontSize: 12 }}
        />
      );
    }

    return (
      <span
        style={{ cursor: isAdmin ? 'pointer' : 'default' }}
        onClick={(e) => {
          e.stopPropagation();
          if (isAdmin) {
            setEditingTeamId(team.teamId);
            setEditValue(team.totalPoints);
          }
        }}>
        {team.totalPoints}
      </span>
    );
  };

  // Split out edit cell rendering
  const renderTotalPointsCell = (match) => {
    if (!match.teamAgainst) {
      return renderEditableTotalValue(match);
    }

    return (
      <Box display="flex" flexDirection={'column'} alignItems={'center'} gap={0.25}>
        {renderEditableTotalValue(match.teamFor)}
        {renderEditableTotalValue(match.teamAgainst)}
      </Box>
    );
  };

  const renderNetCell = (match) => {
    if (!match.teamAgainst) {
      return `${match.net}`;
    }

    return (
      <Box display="flex" flexDirection={'column'} alignItems={'center'} gap={0.25}>
        <Typography fontSize={overlayScoreValueSize}>{match.teamFor.net}</Typography>
        <Typography fontSize={overlayScoreValueSize}>{match.teamAgainst.net}</Typography>
      </Box>
    );
  };

  // Handler for saving the new value
  const handleSave = async (teamId, game) => {
    if (editValue === '' || isNaN(editValue)) {
      setEditingTeamId(null);
      return;
    }

    const result = await dispatch(
      updateTotalPoints({ gameId: game, teamId, totalPoints: Number(editValue) })
    );

    // If dispatch was successful, update localTeams
    if (!result.error) {
      setLocalTeams((prevTeams) =>
        prevTeams.map((team) =>
          team.teamId === teamId ? { ...team, totalPoints: Number(editValue) } : team
        )
      );
    }

    setEditingTeamId(null);
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: 1,
        maxWidth: isOverlay ? 590 : 430,
        width: isOverlay ? '100%' : 'auto',
        border: '1px solid',
        borderColor: 'primary.main',
        ...(isOverlay
          ? {
              backgroundColor: 'rgba(10, 18, 32, 0.8)',
              backgroundImage: 'none',
              backdropFilter: 'blur(2px)'
            }
          : {})
      }}>
      <Table
        padding="none"
        size={isOverlay ? 'medium' : 'small'}
        sx={
          isOverlay
            ? {
                backgroundColor: 'rgba(10, 18, 32, 0.8)',
                '& .MuiTableCell-root': {
                  fontSize: overlayFontSize,
                  py: 1,
                  textAlign: 'center'
                }
              }
            : undefined
        }>
        <TableHead>
          <TableRow>
            <StyledTableCell
              padding="normal"
              align={isOverlay ? 'center' : 'left'}
              sx={{ fontWeight: 'bold', fontSize: overlayHeaderFontSize }}>
              {headerLabel}
              {roundScheduleText && (
                <Typography
                  component="div"
                  fontWeight={'normal'}
                  sx={{ fontSize: isOverlay ? 12 : 10, lineHeight: 1.3 }}>
                  {roundScheduleText}
                </Typography>
              )}
            </StyledTableCell>
            <StyledTableCell
              padding="normal"
              align={'center'}
              sx={{ fontSize: overlayHeaderFontSize }}>
              Score
            </StyledTableCell>
            {showExtraEndColumn && (
              <StyledTableCell
                padding="normal"
                align={'center'}
                sx={{ fontSize: overlayHeaderFontSize }}>
                Ends
              </StyledTableCell>
            )}
            <StyledTableCell
              padding="normal"
              align={'center'}
              sx={{ fontSize: overlayHeaderFontSize }}>
              Total
            </StyledTableCell>
            <StyledTableCell
              padding="normal"
              align={'center'}
              sx={{ fontSize: overlayHeaderFontSize }}>
              Net
            </StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {matchRows.map((team, index) => {
            const clickable =
              typeof isGameClickable === 'function' ? isGameClickable(team) : true;

            const handleClick = () => {
              if (!clickable) return;

              if (typeof onGameClick === 'function') {
                onGameClick(team);
                return;
              }

              navigate(
                authenticated
                  ? `/game/scoring/${team.game}`
                  : `/embed/games/${team.game}`
              );
            };

            return (
              <TableRow
                key={index}
                sx={{
                  cursor: clickable ? 'pointer' : 'default',
                  ...(clickable
                    ? {
                        '&:hover': {
                          backgroundColor: 'primary.main',
                          color: '#fff',
                          '& *:not(input)': {
                            color: '#fff'
                          }
                        }
                      }
                    : {})
                }}
                onClick={handleClick}>
                <StyledTableCell padding="normal" align={isOverlay ? 'center' : 'left'}>
                  <Typography
                    fontSize={9}
                    color="grey.700"
                    textAlign={isOverlay ? 'center' : 'left'}>
                    {team.lastGamePlayedAt
                      ? new Date(team.lastGamePlayedAt).toLocaleDateString()
                      : null}
                  </Typography>
                  <Typography
                    fontSize={overlayTeamNameSize}
                    fontWeight={'bold'}
                    textAlign={isOverlay ? 'center' : 'left'}
                    color={`${team.teamFor?.colors?.fColor || 'primary'}.main`}>
                    {team.teamFor?.teamName || team.teamName}
                  </Typography>
                  <Typography
                    fontSize={overlayTeamAgainstSize}
                    fontStyle={'italic'}
                    color={`${team.teamAgainst?.colors?.fColor || 'primary'}.main`}
                    textAlign={isOverlay ? 'center' : 'left'}>
                    {team.teamAgainst?.teamName || team.teamAgainstName}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell align={'center'}>{setBox(team)}</StyledTableCell>
                {showExtraEndColumn && (
                  <StyledTableCell align={'center'}>{renderExtraEndCell(team)}</StyledTableCell>
                )}
                <StyledTableCell align={'center'}>{renderTotalPointsCell(team)}</StyledTableCell>
                <StyledTableCell align={'center'}>{renderNetCell(team)}</StyledTableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SectionScoreCard;
